import type { AdminSettings, LocalizedPair, SocialLink } from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import { SOCIAL_PLATFORMS } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/app-shell';
import { OptionalBilingualField } from '@/components/bilingual';
import { Alert, Button, Card, Checkbox, Field, Input, Modal, Select, SkeletonRows } from '@/components/ui';
import { apiGet, apiPatch, useAdminMutation } from '@/hooks/admin';
import { ApiError, countsFromError } from '@/lib/errors';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';

const URL = '/admin/settings';

export function SettingsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');
  const isOwner = can('OWNER');

  const query = useQuery({ queryKey: qk.settings, queryFn: () => apiGet<AdminSettings>(URL) });
  const settings = query.data?.data;

  const [brandName, setBrandName] = useState<LocalizedPair>({ ar: '', en: '' });
  const [footer, setFooter] = useState<LocalizedPair>({ ar: '', en: '' });
  const [seoTitle, setSeoTitle] = useState<LocalizedPair>({ ar: '', en: '' });
  const [seoDescription, setSeoDescription] = useState<LocalizedPair>({ ar: '', en: '' });
  const [priceNote, setPriceNote] = useState<LocalizedPair>({ ar: '', en: '' });
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [social, setSocial] = useState<SocialLink[]>([]);

  useEffect(() => {
    if (!settings) return;
    setBrandName(settings.brandName);
    setFooter(editablePair(settings.footer));
    setSeoTitle(editablePair(settings.seo.title));
    setSeoDescription(editablePair(settings.seo.description));
    setPriceNote(editablePair(settings.priceNote));
    setPhone(settings.contact.phone ?? '');
    setEmail(settings.contact.email ?? '');
    setWhatsapp(settings.contact.whatsapp ?? '');
    setSocial(settings.socialLinks);
  }, [settings]);

  const save = useAdminMutation(
    () =>
      apiPatch(`${URL}/public-content`, {
        expectedVersion: settings?.version ?? 1,
        brandNameAr: brandName.ar,
        brandNameEn: brandName.en,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        whatsapp: whatsapp.trim() || undefined,
        socialLinks: social,
        footerAr: footer.ar,
        footerEn: footer.en,
        seoTitleAr: seoTitle.ar,
        seoTitleEn: seoTitle.en,
        seoDescAr: seoDescription.ar,
        seoDescEn: seoDescription.en,
        priceNoteAr: priceNote.ar,
        priceNoteEn: priceNote.en,
      }),
    { successMessage: t('common.saved'), invalidate: [qk.settings, qk.readiness] },
  );

  if (query.isLoading || !settings) return <SkeletonRows rows={6} />;

  return (
    <>
      <PageHeader
        width="narrow"
        title={t('settings.title')}
        actions={
          canEdit && (
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
              {t('common.save')}
            </Button>
          )
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <Card title={t('settings.brand')}>
            <OptionalBilingualField
              label={t('settings.brandName')}
              value={brandName}
              onChange={setBrandName}
              maxLength={150}
              disabled={!canEdit}
            />
          </Card>

          <Card title={t('settings.contact')}>
            <div className="flex flex-col gap-4">
              <Field label={t('settings.phone')} htmlFor="phone">
                <Input id="phone" type="tel" dir="ltr" disabled={!canEdit} value={phone} onChange={(event) => setPhone(event.target.value)} />
              </Field>
              <Field label={t('settings.email')} htmlFor="settings-email">
                <Input id="settings-email" type="email" dir="ltr" disabled={!canEdit} value={email} onChange={(event) => setEmail(event.target.value)} />
              </Field>
              <Field label={t('settings.whatsapp')} htmlFor="whatsapp">
                <Input id="whatsapp" type="tel" dir="ltr" disabled={!canEdit} value={whatsapp} onChange={(event) => setWhatsapp(event.target.value)} />
              </Field>
            </div>
          </Card>

          <Card title={t('settings.social')} description={t('settings.socialHint')}>
            <ul className="flex flex-col gap-3">
              {social.map((link, index) => (
                <li key={index} className="flex flex-wrap items-end gap-2">
                  <Field label={t('settings.socialPlatform')} className="flex-none">
                    <Select
                      aria-label={t('settings.socialPlatform')}
                      disabled={!canEdit}
                      value={link.platform}
                      onChange={(event) =>
                        setSocial((current) =>
                          current.map((row, position) =>
                            position === index ? { ...row, platform: event.target.value } : row,
                          ),
                        )
                      }
                    >
                      {SOCIAL_PLATFORMS.map((platform) => (
                        <option key={platform} value={platform}>
                          {platform}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  {/* A basis rather than `min-w-0`: the row already wraps, and a
                      URL field that can shrink to nothing does not wrap — it
                      collapses. Reserving the chevron's gutter widened the
                      platform select by 24px, which took this from 52px to 28px
                      on a phone. It now moves to its own line instead. */}
                  <Field label={t('settings.socialUrl')} className="flex-1 basis-56">
                    <Input
                      type="url"
                      dir="ltr"
                      // `Field` now associates its label, but three rows all
                      // labelled "URL" is still a list of identical fields to
                      // anyone reading them out of context. The platform is
                      // data, not a string to translate, so it is appended.
                      aria-label={`${t('settings.socialUrl')} — ${link.platform}`}
                      placeholder="https://"
                      disabled={!canEdit}
                      value={link.url}
                      onChange={(event) =>
                        setSocial((current) =>
                          current.map((row, position) =>
                            position === index ? { ...row, url: event.target.value } : row,
                          ),
                        )
                      }
                    />
                  </Field>

                  {/* Disabled links are kept rather than deleted: turning one
                      off for a season should not lose the URL. */}
                  <Checkbox
                    checked={link.enabled}
                    disabled={!canEdit}
                    label={t('settings.socialEnabled')}
                    onChange={(checked) =>
                      setSocial((current) =>
                        current.map((row, position) =>
                          position === index ? { ...row, enabled: checked } : row,
                        ),
                      )
                    }
                  />

                  {canEdit && (
                    <Button
                      size="sm"
                      iconOnly
                      variant="ghost"
                      aria-label={t('common.remove')}
                      onClick={() => setSocial((current) => current.filter((_, position) => position !== index))}
                    >
                      <Trash2 className="dio-icon" aria-hidden="true" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>

            {canEdit && (
              <Button
                size="sm"
                variant="ghost"
                className="mt-3"
                onClick={() =>
                  setSocial((current) => [...current, { platform: 'instagram', url: '', enabled: true }])
                }
              >
                <Plus className="dio-icon" aria-hidden="true" />
                {t('settings.addSocial')}
              </Button>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          <Card title={t('settings.seo')}>
            <div className="flex flex-col gap-4">
              <OptionalBilingualField label={t('settings.seoTitle')} value={seoTitle} onChange={setSeoTitle} maxLength={70} disabled={!canEdit} />
              <OptionalBilingualField label={t('settings.seoDescription')} value={seoDescription} onChange={setSeoDescription} multiline rows={3} maxLength={160} disabled={!canEdit} />
            </div>
          </Card>

          <Card title={t('settings.footer')}>
            <div className="flex flex-col gap-4">
              <OptionalBilingualField label={t('settings.footer')} value={footer} onChange={setFooter} multiline rows={3} maxLength={500} disabled={!canEdit} />
              <OptionalBilingualField label={t('settings.priceNote')} hint={t('settings.priceNoteHint')} value={priceNote} onChange={setPriceNote} maxLength={200} disabled={!canEdit} />
            </div>
          </Card>

          <RegionalCard settings={settings} isOwner={isOwner} />
        </div>
      </div>
    </>
  );
}

/**
 * Currency and default time zone.
 *
 * Owner only, and the currency change is confirmation-gated: it converts
 * nothing. Every stored price keeps its number and changes meaning, which is a
 * decision about the whole menu rather than a settings tweak.
 */
function RegionalCard({ settings, isOwner }: { settings: AdminSettings; isOwner: boolean }) {
  const { t } = useTranslation();
  const [currency, setCurrency] = useState(settings.regional.currencyCode ?? '');
  const [timezone, setTimezone] = useState(settings.regional.defaultTimezone ?? '');
  const [confirming, setConfirming] = useState<Record<string, number> | null>(null);

  const save = useAdminMutation(
    (confirm: boolean) =>
      apiPatch(`${URL}/regional`, {
        expectedVersion: settings.version,
        currencyCode: currency.trim().toUpperCase() || undefined,
        defaultTimezone: timezone.trim() || undefined,
        ...(confirm ? { confirmCurrencyChange: true } : {}),
      }),
    {
      successMessage: t('common.saved'),
      invalidate: [qk.settings, qk.readiness, qk.dashboard],
      onSuccess: () => setConfirming(null),
      onError: (error) => {
        if (error instanceof ApiError && error.code === 'CURRENCY_CHANGE_NEEDS_CONFIRMATION') {
          setConfirming(countsFromError(error, 'currencyCode'));
          return true;
        }
      },
    },
  );

  if (!isOwner) {
    return (
      <Card title={t('settings.regional')}>
        <p className="dio-meta">{t('settings.regionalOwnerOnly')}</p>
        <p className="tabular mt-2">
          {settings.regional.currencyCode ?? t('common.notSet')} ·{' '}
          {settings.regional.defaultTimezone ?? t('common.notSet')}
        </p>
      </Card>
    );
  }

  return (
    <Card title={t('settings.regional')} description={t('settings.regionalOwnerOnly')}>
      <div className="flex flex-col gap-4">
        {!settings.regional.currencyCode && <Alert tone="warning">{t('errors.MENU_NOT_CONFIGURED')}</Alert>}

        <Field label={t('settings.currency')} htmlFor="currency" hint={t('settings.currencyHint')}>
          <Input
            id="currency"
            dir="ltr"
            maxLength={3}
            /*
             * The example lives in the translated hint, not in a placeholder.
             * A placeholder here would be untranslated prose — the i18n guard
             * says so, and it is right: "SAR" is not a technical token the way
             * an IANA zone is, and the guard exists to catch exactly this.
             *
             * It needs an example at all because the field takes the ISO code
             * and not the currency's name: typing "ريال سعودي" is cut to three
             * characters by maxLength and then refused as INVALID_CURRENCY.
             */
            className="tabular uppercase"
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
          />
        </Field>

        <Field label={t('settings.defaultTimezone')} htmlFor="default-tz" hint={t('branches.timezoneHint')}>
          <Input
            id="default-tz"
            dir="ltr"
            placeholder="Asia/Riyadh"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
          />
        </Field>

        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(false)}>
          {t('common.save')}
        </Button>
      </div>

      <Modal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={t('settings.currencyChangeTitle')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" loading={save.isPending} onClick={() => save.mutate(true)}>
              {t('settings.currencyChangeConfirm')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <Alert tone="warning">{t('settings.currencyChangeBody')}</Alert>
          <p className="tabular">
            {t('settings.currencyChangeAffects', {
              items: confirming?.AFFECTS_ITEMS ?? 0,
              addons: confirming?.AFFECTS_ADDONS ?? 0,
              offers: confirming?.AFFECTS_OFFER_PRICES ?? 0,
            })}
          </p>
        </div>
      </Modal>
    </Card>
  );
}
